import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spinner, Table, Badge } from 'react-bootstrap';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate, fmtNum } from '../utils/format';

const Stat = ({ label, value, icon, color }) => (
  <Card className="stat-card h-100" style={{ borderLeftColor: color }}>
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <div className="stat-label">{label}</div>
          <div className="stat-value">{value}</div>
        </div>
        <i className={`bi ${icon}`} style={{ fontSize: '1.75rem', color }}></i>
      </div>
    </Card.Body>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([client.get('/dashboard/summary'), client.get('/dashboard/recent')])
      .then(([s, r]) => {
        setSummary(s.data);
        setRecent(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner animation="border" />;
  if (!summary) return <div>Could not load dashboard</div>;

  return (
    <div>
      <h4 className="mb-3">
        Welcome back, {user.name} <small className="text-muted">({user.role})</small>
      </h4>

      <Row className="g-3">
        <Col md={3}>
          <Stat label="Open Leads" value={summary.leads.open} icon="bi-person-lines-fill" color="#0d6efd" />
        </Col>
        <Col md={3}>
          <Stat label="Total Bookings" value={summary.bookings.count} icon="bi-house-check" color="#198754" />
        </Col>
        <Col md={3}>
          <Stat
            label="Payment Received"
            value={fmtMoney(summary.bookings.totalReceived)}
            icon="bi-cash-stack"
            color="#0dcaf0"
          />
        </Col>
        <Col md={3}>
          <Stat
            label="Payment Pending"
            value={fmtMoney(summary.bookings.pending)}
            icon="bi-hourglass-split"
            color="#dc3545"
          />
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={3}>
          <Stat label="Booking Value (Total)" value={fmtMoney(summary.bookings.grandTotal)} icon="bi-bank2" color="#6610f2" />
        </Col>
        <Col md={3}>
          <Stat label="In Account" value={fmtMoney(summary.bookings.receivedAccount)} icon="bi-wallet2" color="#20c997" />
        </Col>
        <Col md={3}>
          <Stat label="In PDC / Cash" value={fmtMoney(summary.bookings.receivedPDC)} icon="bi-cash" color="#fd7e14" />
        </Col>
        <Col md={3}>
          <Stat label="Overdue (Due)" value={fmtMoney(summary.bookings.due)} icon="bi-exclamation-triangle" color="#dc3545" />
        </Col>
      </Row>

      {(user.role === 'sales' || user.role === 'admin' || user.role === 'support') && (
        <Row className="g-3 mt-1">
          <Col md={3}>
            <Stat label="Commission Paid" value={fmtMoney(summary.commissions.paid)} icon="bi-check-circle" color="#198754" />
          </Col>
          <Col md={3}>
            <Stat
              label="Commission Pending"
              value={fmtMoney(summary.commissions.pending)}
              icon="bi-clock-history"
              color="#ffc107"
            />
          </Col>
          <Col md={3}>
            <Stat label="Total Area Sold (sq.ft)" value={fmtNum(summary.bookings.totalAreaSqft)} icon="bi-rulers" color="#6c757d" />
          </Col>
          <Col md={3}>
            <Stat label="Bookings With Overdue" value={summary.bookings.overdue} icon="bi-bell" color="#dc3545" />
          </Col>
        </Row>
      )}

      {recent && (
        <Row className="g-3 mt-3">
          <Col lg={6}>
            <Card>
              <Card.Header><strong>Recent Leads</strong></Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive size="sm" className="mb-0">
                  <thead><tr><th>Customer</th><th>Project</th><th>Status</th><th>Created</th></tr></thead>
                  <tbody>
                    {recent.recentLeads.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-muted py-3">No leads yet</td></tr>
                    )}
                    {recent.recentLeads.map((l) => (
                      <tr key={l._id}>
                        <td>{l.customerName}<br /><small className="text-muted">{l.customerPhone}</small></td>
                        <td>{l.project?.name || '-'}</td>
                        <td><Badge bg="info" className="text-uppercase">{l.status}</Badge></td>
                        <td>{fmtDate(l.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <Card.Header><strong>Recent Bookings</strong></Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive size="sm" className="mb-0">
                  <thead><tr><th>Booking #</th><th>Unit</th><th>Customer</th><th>Total</th></tr></thead>
                  <tbody>
                    {recent.recentBookings.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-muted py-3">No bookings yet</td></tr>
                    )}
                    {recent.recentBookings.map((b) => (
                      <tr key={b._id}>
                        <td>{b.bookingNo}</td>
                        <td>{b.unitNo}<br /><small className="text-muted">{b.project?.name}</small></td>
                        <td>{b.customerName}</td>
                        <td>{fmtMoney(b.grandTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
